import { Component } from '@theme/component';

const API_BASE = '/apps/cavador-visualizer/client';
const UUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i;
const text = (value, length = 160) => typeof value === 'string' ? value.trim().slice(0, length) : '';

export function normalizeClientLink(value, optional = false) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw && optional) return '';
  if (!raw || raw.length > 2000 || /[\u0000-\u0020\u007f]/.test(raw)) throw new Error('invalid_link');
  const candidate = raw.startsWith('//') ? `https:${raw}` : /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  const url = new URL(candidate);
  if (!['https:', 'http:'].includes(url.protocol) || !url.hostname.includes('.') || url.username || url.password) throw new Error('invalid_link');
  return url.href;
}

export function claimPayload(form, requestId, language) {
  const get = name => form.elements.namedItem(name);
  return {
    requestId,
    name: text(get('contact[name]')?.value),
    email: text(get('contact[email]')?.value, 254),
    orderReference: text(get('contact[Order reference]')?.value, 80),
    reviewLink: normalizeClientLink(get('contact[Posted review link]')?.value),
    mediaLink: normalizeClientLink(get('contact[Project photo or video link]')?.value, true),
    publicationPermission: get('contact[Optional project publication permission]')?.checked === true,
    confirmation: get('contact[Honest review and order confirmation]')?.checked === true,
    language: language?.startsWith('fr') ? 'fr' : 'en'
  };
}

function node(tag, className, content) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (content !== undefined) element.textContent = content;
  return element;
}

if (!customElements.get('cavador-client-claims')) {
  customElements.define('cavador-client-claims', class extends Component {
    connectedCallback() { super.connectedCallback(); this.initialize(); }
    updatedCallback() { super.updatedCallback(); this.initialize(); }
    disconnectedCallback() {
      this.controller?.abort();
      this.quarantine();
      super.disconnectedCallback();
    }
    initialize() {
      this.controller?.abort();
      this.requests?.forEach(request => request.abort());
      this.controller = new AbortController();
      this.requests = new Set();
      this.generation = (this.generation || 0) + 1;
      const owner = this.dataset.customer || '';
      if (this.owner !== owner) { this.pendingClaim = null; this.claimAttempted = false; this.emailMode = false; this.nativePermission = false; this.nativeSent = false; this.nativeAttempted = false; this.guestValidated = false; }
      this.owner = owner;
      this.locale = this.dataset.locale || 'en';
      this.labels = JSON.parse(this.querySelector('[data-client-claims-labels]')?.textContent || '{}');
      this.claims = [];
      this.ready = false;
      this.locked = false;
      this.loading = false;
      this.submitting = false;
      this.gate = document.getElementById(this.dataset.gateId || '');
      this.gateStatus = this.gate?.querySelector('[data-client-privacy-status]');
      this.retry = this.gate?.querySelector('[data-client-privacy-retry]');
      this.signin = this.gate?.querySelector('[data-client-privacy-signin]');
      this.outageEmail = this.gate?.querySelector('[data-client-privacy-email]');
      this.claimStatus = this.querySelector('[data-client-claim-status]');
      this.list = this.querySelector('[data-client-claim-list]');
      this.form = this.querySelector('form');
      this.querySelector('[data-client-claim-tracking]')?.removeAttribute('hidden');
      if (!this.form) return;
      const options = { signal: this.controller.signal };
      this.retry?.addEventListener('click', () => this.loadClaims(), options);
      this.outageEmail?.addEventListener('click', () => {
        if (!this.fallbackVerified || this.claimAttempted || this.pendingClaim || this.locked) return;
        this.emailMode = true;
        this.hidden = false;
        if (this.gate) this.gate.hidden = true;
        this.status(this.querySelector('[data-client-claim-mode]'), 'email_fallback');
        this.status(this.claimStatus, 'email_fallback');
      }, options);
      this.querySelector('[data-client-claims-refresh]')?.addEventListener('click', () => this.loadClaims(), options);
      window.addEventListener('pagehide', () => this.quarantine(), options);
      window.addEventListener('pageshow', () => { if (!document.hidden) this.loadClaims(); }, options);
      document.addEventListener('visibilitychange', () => document.hidden ? this.quarantine() : this.loadClaims(), options);
      window.addEventListener('focus', () => { if (!document.hidden) this.loadClaims(); }, options);
      this.form.addEventListener('submit', event => {
        if (this.hidden || this.locked) { event.preventDefault(); return; }
        if (this.nativePermission && !this.nativeSent) { this.nativePermission = false; this.nativeSent = true; this.nativeEvent = event; return; }
        if (this.nativeSent) { event.preventDefault(); return; }
        if (this.owner && !this.emailMode) { event.preventDefault(); this.submitClaim(); }
      }, { ...options, capture: true });
      const email = this.querySelector('[data-client-email-fallback]');
      if (email) {
        email.hidden = !this.owner || this.claimAttempted || this.emailMode;
        email.addEventListener('click', () => {
          if (!this.ready || this.claimAttempted || this.submitting) return;
          this.emailMode = true;
          email.hidden = true;
          this.status(this.querySelector('[data-client-claim-mode]'), 'email_fallback');
          this.status(this.claimStatus, 'email_fallback');
        }, options);
      }
      this.quarantine();
      if (!document.hidden) this.loadClaims();
    }
    label(key) { return this.labels[key] || ''; }
    status(element, key) { if (element) element.textContent = this.label(key); }
    quarantine() {
      this.generation = (this.generation || 0) + 1;
      this.ready = false;
      this.loading = false;
      this.submitting = false;
      this.fallbackVerified = false;
      this.hidden = true;
      this.claims = [];
      this.requests?.forEach(request => request.abort());
      this.list?.replaceChildren();
      if (this.gate) this.gate.hidden = false;
      this.status(this.gateStatus, 'privacy_checking');
      if (this.retry) this.retry.hidden = true;
      if (this.signin) this.signin.hidden = true;
      if (this.outageEmail) this.outageEmail.hidden = true;
    }
    invalidateIdentity(key) {
      this.quarantine();
      this.locked = true;
      this.pendingClaim = null;
      this.form?.querySelectorAll('input:not([type="hidden"])').forEach(input => {
        input.value = ''; input.defaultValue = ''; input.checked = false; input.removeAttribute('value');
      });
      this.status(this.gateStatus, key);
      if (this.signin) this.signin.hidden = false;
    }
    isCurrent(generation) { return generation === this.generation && this.isConnected && !document.hidden && !this.locked; }
    async request(path, method = 'GET', payload, generation = this.generation) {
      if (this.locked || method !== 'GET' && !this.owner) throw Object.assign(new Error('signin_required'), { status: 401 });
      const controller = new AbortController();
      this.requests.add(controller);
      const timer = window.setTimeout(() => controller.abort(), 30000);
      try {
        const response = await fetch(API_BASE + path, {
          method, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: method === 'GET' ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Cavador-Client': '1', 'X-Cavador-Customer-Context': this.owner },
          ...(payload === undefined ? {} : { body: JSON.stringify(payload) })
        });
        const raw = await response.text();
        if (!this.isCurrent(generation)) throw new Error('stale_request');
        if (raw.length > 256000) throw new Error('invalid_response');
        let result;
        try { result = JSON.parse(raw); } catch { throw new Error('invalid_response'); }
        if (!response.ok) throw Object.assign(new Error(result?.error?.code || 'request_failed'), { status: response.status });
        return result;
      } finally { window.clearTimeout(timer); this.requests.delete(controller); }
    }
    errorKey(error) {
      if (this.locked) return 'account_changed';
      if (error.status === 401) return 'signin_required';
      if (['client_claim_limit', 'client_claim_ledger_full'].includes(error.message)) return 'claim_limit';
      if (error.status === 409) return 'claim_conflict';
      if (error.status === 429) return 'rate_limit';
      if (error.status === 503) return 'unavailable';
      return navigator.onLine === false ? 'offline' : 'request_error';
    }
    async loadClaims() {
      if (this.loading || this.locked || !this.isConnected || document.hidden) return;
      this.quarantine();
      this.loading = true;
      const generation = this.generation;
      try {
        const data = await this.request('/claims', 'GET', undefined, generation);
        if (!this.isCurrent(generation)) return;
        if (String(data.customerId || '').replace('gid://shopify/Customer/', '') !== this.owner || !this.owner) { this.invalidateIdentity('account_changed'); return; }
        if (!Array.isArray(data.claims)) throw new Error('invalid_response');
        this.claims = data.claims.slice(0, 50);
        this.ready = true;
        this.hidden = false;
        if (this.gate) this.gate.hidden = true;
        this.status(this.querySelector('[data-client-claim-mode]'), this.emailMode ? 'email_fallback' : 'tracked_form');
        const submit = this.form.querySelector('[type="submit"]');
        if (submit) submit.disabled = Boolean(this.nativeSent);
        this.renderClaims();
        if (this.pendingClaim) { this.freezeFields(true); this.status(this.claimStatus, 'claim_unconfirmed'); }
      } catch (error) {
        if (!this.isCurrent(generation)) return;
        if (error.status === 401 && error.message === 'customer_login_required' && !this.owner) {
          if (!this.guestValidated) this.form.querySelectorAll('input:not([type="hidden"])').forEach(input => { input.value = ''; input.defaultValue = ''; input.checked = false; });
          this.guestValidated = true;
          this.hidden = false;
          if (this.gate) this.gate.hidden = true;
          this.status(this.querySelector('[data-client-claim-mode]'), 'email_fallback');
          this.status(this.claimStatus, 'signin_claims');
        } else if (error.status === 401) this.invalidateIdentity('signin_required');
        else {
          this.status(this.gateStatus, 'privacy_error');
          if (this.retry) this.retry.hidden = false;
          if (!this.claimAttempted && !this.pendingClaim) await this.verifyEmailFallback(generation);
        }
      } finally { if (generation === this.generation) this.loading = false; }
    }
    async verifyEmailFallback(generation) {
      try {
        const session = await this.request('/session', 'GET', undefined, generation);
        if (!this.isCurrent(generation)) return;
        if (String(session.customerId || '').replace('gid://shopify/Customer/', '') !== this.owner || !this.owner) { this.invalidateIdentity('account_changed'); return; }
        this.fallbackVerified = true;
        this.status(this.gateStatus, 'tracking_unavailable');
        if (this.outageEmail) this.outageEmail.hidden = false;
      } catch (error) { if (this.isCurrent(generation) && error.status === 401) this.invalidateIdentity('signin_required'); }
    }
    date(value) { try { return new Intl.DateTimeFormat(this.locale, { dateStyle: 'medium' }).format(new Date(value)); } catch { return ''; } }
    freezeFields(frozen) {
      if (frozen) {
        if (this.fieldStates) return;
        this.fieldStates = [...this.form.querySelectorAll('input:not([type="hidden"])')].map(input => ({ input, disabled: input.disabled, readOnly: input.readOnly }));
        for (const { input } of this.fieldStates) { if (input.type === 'checkbox') input.disabled = true; else input.readOnly = true; }
      } else {
        for (const { input, disabled, readOnly } of this.fieldStates || []) { input.disabled = disabled; input.readOnly = readOnly; }
        this.fieldStates = null;
      }
    }
    sendNativeEmail(claim) {
      if (this.nativeSent || this.nativeAttempted || this.hidden || this.locked) return;
      let reference = this.form.querySelector('[data-client-ledger-reference]');
      if (!reference) { reference = node('input'); reference.type = 'hidden'; reference.name = 'contact[Account claim reference]'; reference.setAttribute('data-client-ledger-reference', ''); this.form.append(reference); }
      reference.value = claim.id;
      const body = this.form.elements.namedItem('contact[body]');
      if (body) {
        this.baseBody ??= body.value;
        body.value = `${this.baseBody}\n\n${this.label('claim_reference')}: ${claim.id}`;
      }
      this.freezeFields(false);
      if (!this.form.reportValidity() || typeof this.form.requestSubmit !== 'function') { this.status(this.claimStatus, 'email_pending'); return; }
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) submit.disabled = false;
      this.nativePermission = true;
      this.nativeAttempted = true;
      try {
        this.form.requestSubmit(submit || undefined);
        queueMicrotask(() => {
          if (this.nativeEvent?.defaultPrevented && this.isConnected && !this.locked) this.status(this.claimStatus, 'email_pending');
          if (submit && this.nativeSent) submit.disabled = true;
        });
      }
      catch { this.nativePermission = false; this.status(this.claimStatus, 'email_pending'); }
    }
    renderClaims() {
      if (!this.list || this.locked || this.hidden) return;
      const statuses = ['received', 'under_review', 'credit_issued', 'needs_info', 'declined'];
      this.list.replaceChildren(...this.claims.filter(claim => UUID.test(claim?.id || '') && statuses.includes(claim.status)).map(claim => {
        const item = node('li', 'cac__claim');
        const top = node('div', 'cac__claim-top');
        top.append(node('h4', '', `${this.label('order')} ${text(claim.orderReference, 80)}`), node('span', 'cac__badge', this.label(`status_${claim.status}`)));
        item.append(top, node('p', 'cac__claim-meta', `${this.label('claim_reference')} ${claim.id.slice(0, 8)} · ${this.date(claim.submittedAt || claim.createdAt)}`));
        const money = cents => new Intl.NumberFormat(this.locale, { style: 'currency', currency: 'CAD', currencyDisplay: 'code' }).format(cents / 100);
        if (Number.isSafeInteger(claim.requestedCreditCents) && claim.requestedCreditCents >= 0 && claim.currency === 'CAD') item.append(node('p', '', `${this.label('requested')} ${money(claim.requestedCreditCents)}`));
        if (claim.status === 'credit_issued' && Number.isSafeInteger(claim.issuedCreditCents) && claim.issuedCreditCents >= 0 && claim.currency === 'CAD') item.append(node('p', '', `${this.label('issued')} ${money(claim.issuedCreditCents)}`));
        const timeline = node('ol', 'cac__timeline');
        const current = claim.status === 'credit_issued' ? 2 : claim.status === 'received' ? 0 : 1;
        for (const [index, state] of ['received', 'under_review', 'credit_issued'].entries()) {
          const step = node('li', index <= current ? 'is-complete' : '', this.label(`status_${state}`));
          if (index === current && ['received', 'under_review', 'credit_issued'].includes(claim.status)) step.setAttribute('aria-current', 'step');
          timeline.append(step);
        }
        item.append(timeline);
        if (claim.publicNote) item.append(node('p', '', text(claim.publicNote, 2000)));
        return item;
      }));
      if (!this.pendingClaim && !this.emailMode) this.status(this.claimStatus, this.claims.length ? 'manual_review' : 'empty_claims');
    }
    async submitClaim() {
      if (this.submitting || this.hidden || this.locked || !this.ready) return;
      let payload;
      try {
        this.form.querySelectorAll('[data-reward-link]').forEach(input => {
          input.setCustomValidity('');
          try { input.value = normalizeClientLink(input.value, !input.required); } catch { input.setCustomValidity(this.label('invalid_link')); }
        });
        if (!this.form.reportValidity()) return;
        payload = claimPayload(this.form, this.pendingClaim?.requestId || crypto.randomUUID(), this.locale);
        if (!payload.confirmation) return;
        if (this.pendingClaim && JSON.stringify(payload) !== JSON.stringify(this.pendingClaim)) { this.status(this.claimStatus, 'retry_same_claim'); return; }
      } catch { this.status(this.claimStatus, 'invalid_link'); return; }
      this.submitting = true; this.claimAttempted = true; this.pendingClaim = payload;
      this.freezeFields(true);
      const generation = this.generation;
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      this.querySelector('[data-client-email-fallback]')?.setAttribute('hidden', '');
      this.status(this.claimStatus, 'submitting_claim');
      try {
        const data = await this.request('/claims', 'POST', payload, generation);
        if (!this.isCurrent(generation)) return;
        if (!UUID.test(data.claim?.id || '')) throw new Error('invalid_response');
        this.claims = [data.claim, ...this.claims.filter(claim => claim.id !== data.claim.id)];
        this.pendingClaim = null;
        this.renderClaims();
        this.status(this.claimStatus, 'claim_received');
        this.sendNativeEmail(data.claim);
      } catch (error) {
        if (!this.isCurrent(generation)) return;
        if (error.message === 'customer_context_changed') { this.invalidateIdentity('account_changed'); return; }
        if (error.status === 401) { this.invalidateIdentity('signin_required'); return; }
        if (error.status && error.status < 500) { this.pendingClaim = null; this.freezeFields(false); }
        this.status(this.claimStatus, this.pendingClaim ? 'claim_unconfirmed' : this.errorKey(error));
      } finally {
        if (generation === this.generation) { this.submitting = false; if (submit) submit.disabled = this.locked || this.nativeSent; }
      }
    }
  });
}
